/**
 * FamilySetup Widget
 *
 * Family configuration widget: whether bringing family, add family program.
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@tonnie/ui';
import { CheckCircle2, Info, Sparkles, UserPlus, Users } from '@tonnie/assets';
import { useCallback, useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

interface IFamilySetupConfig {
  /**
   * The onboarding family answer, when there is one (Citizen first run slice
   * C — the household task reads the profile first). Pre-selects the choice;
   * the citizen still confirms it.
   */
  bringFamily?: boolean;
}

/**
 * FamilySetupWidget - Family configuration widget for the journey builder.
 *
 * Asks whether the user is bringing family members and, if so, offers an optional
 * Family Program add-on. Shows a summary of the selection and requires explicit
 * confirmation before calling `onSelect`.
 *
 * @param config - The guide's variables; `bringFamily` pre-selects the answer on file
 * @param onSelect - Callback fired with `{ bringFamily, familyProgram, hasPartner, hasChildren }` on confirm
 * @returns React component for family setup
 */
export function FamilySetupWidget({
  config,
  onSelect,
}: WidgetProps & { config?: IFamilySetupConfig; onSelect?: (value: unknown) => void }) {
  const { t } = useTranslation();
  const fromProfile = typeof config?.bringFamily === 'boolean' ? config.bringFamily : null;
  const [bringFamily, setBringFamily] = useState<boolean | null>(fromProfile);

  const handleBringFamily = useCallback((value: boolean) => {
    setBringFamily(value);
  }, []);

  const handleConfirm = useCallback(() => {
    if (bringFamily === null) return;
    onSelect?.({
      bringFamily,
      familyProgram: false,
      hasPartner: bringFamily,
      hasChildren: false,
    });
  }, [bringFamily, onSelect]);

  return (
    <div className="w-full space-y-4 py-2" data-testid="widget-family_setup">
      {/* Main question */}
      <Card className="border-2 border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-accessible" />
            {t('copilot.bringFamily')}
          </CardTitle>
          <CardDescription>{t('copilot.familyJoinQuestion')}</CardDescription>
          {fromProfile !== null && bringFamily === fromProfile && (
            <p
              className="flex items-center gap-1.5 pt-1 text-xs text-muted-foreground"
              data-testid="widget-family_setup-from-profile"
            >
              <Info className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t('copilot.familyFromProfile')}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant={bringFamily === true ? 'default' : 'outline'}
              className="h-auto py-4 flex flex-col items-center gap-2"
              aria-pressed={bringFamily === true}
              onClick={() => handleBringFamily(true)}
              data-testid="widget-family_setup-partner"
            >
              <Users
                className={cn(
                  'w-6 h-6',
                  bringFamily === true ? 'text-primary-foreground' : 'text-primary-accessible'
                )}
              />
              <span className="font-semibold text-sm">{t('copilot.yesBringingFamily')}</span>
            </Button>
            <Button
              variant={bringFamily === false ? 'default' : 'outline'}
              className="h-auto py-4 flex flex-col items-center gap-2"
              aria-pressed={bringFamily === false}
              onClick={() => handleBringFamily(false)}
              data-testid="widget-family_setup-solo"
            >
              <UserPlus
                className={cn(
                  'w-6 h-6',
                  bringFamily === false ? 'text-primary-foreground' : 'text-muted-foreground'
                )}
              />
              <span className="font-semibold text-sm">{t('copilot.justMe')}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Family Program card — Coming Soon (feature-flagged for future release) */}
      {bringFamily === true && (
        <Card className="border-2 border-border">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <CardTitle className="text-base flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-muted-foreground" />
                  {t('copilot.familyProgramAddon')}
                </CardTitle>
                <CardDescription className="text-sm">
                  {t('copilot.familyProgramDesc')}
                </CardDescription>
              </div>
              <Badge
                variant="outline"
                className="shrink-0 text-primary-accessible border-primary/30"
              >
                {t('common.comingSoon')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5 text-sm text-muted-foreground">
              {[
                t('copilot.familyFeature1'),
                t('copilot.familyFeature2'),
                t('copilot.familyFeature3'),
                t('copilot.familyFeature4'),
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground/50 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <Info className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">{t('copilot.familyComingSoonNote')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary + Confirm */}
      {bringFamily !== null && (
        <>
          <div className="p-3 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary-accessible" />
              <span className="font-medium">
                {bringFamily ? t('copilot.familyJoining') : t('copilot.individualJourney')}
              </span>
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={handleConfirm} data-testid="widget-family_setup-confirm">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              {t('common.confirm')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
